import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Building, Users, Settings, Trash2, CheckCircle, AlertCircle } from 'lucide-react';

export function OrganizationSettings() {
  const { userProfile, updateProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formData, setFormData] = useState({
    organizationName: '',
    description: '',
    companySize: '',
    companyIndustry: ''
  });

  // Initialize form data from user profile
  useEffect(() => {
    if (userProfile) {
      setFormData({
        organizationName: userProfile.companyName || '',
        description: userProfile.companyDescription || '',
        companySize: userProfile.companySize || '',
        companyIndustry: userProfile.companyIndustry || ''
      });
    }
  }, [userProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSaveMessage(null);
    
    try {
      // Update user profile company info with correct backend field names
      const updateData = {
        company_name: formData.organizationName,
        company_size: formData.companySize,
        company_industry: formData.companyIndustry
        // Note: company_description and organization fields will need backend support
      };

      const { error } = await updateProfile(updateData);
      
      if (error) {
        setSaveMessage({ type: 'error', message: error.message || 'Failed to update organization' });
      } else {
        setSaveMessage({ type: 'success', message: 'Organization settings updated successfully!' });
        // Clear success message after 5 seconds
        setTimeout(() => setSaveMessage(null), 5000);
      }
    } catch (error) {
      console.error('Organization update error:', error);
      setSaveMessage({ type: 'error', message: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear save message when user starts typing
    if (saveMessage) {
      setSaveMessage(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Save Message */}
      {saveMessage && (
        <div className={`flex items-center space-x-2 p-3 rounded-lg ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          {saveMessage.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{saveMessage.message}</span>
        </div>
      )}

      <Card className="p-6">
        <div className="flex items-center mb-6">
          <Building className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Organization Details</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="organizationName">Organization Name</Label>
            <Input 
              id="organizationName"
              value={formData.organizationName}
              onChange={(e) => handleInputChange('organizationName', e.target.value)}
              placeholder="Enter organization name"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <textarea 
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              placeholder="Describe your organization..."
              maxLength={500}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.description.length}/500 characters
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="companySize">Company Size</Label>
              <select
                id="companySize"
                value={formData.companySize}
                onChange={(e) => handleInputChange('companySize', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select company size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1000+">1000+ employees</option>
              </select>
            </div>

            <div>
              <Label htmlFor="companyIndustry">Industry</Label>
              <select
                id="companyIndustry"
                value={formData.companyIndustry}
                onChange={(e) => handleInputChange('companyIndustry', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select industry</option>
                <option value="Technology">Technology</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Finance">Finance</option>
                <option value="Education">Education</option>
                <option value="Retail">Retail</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Consulting">Consulting</option>
                <option value="Marketing">Marketing</option>
                <option value="Real Estate">Real Estate</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => {
                // Reset form to original values
                if (userProfile) {
                  setFormData({
                    organizationName: userProfile.companyName || '',
                    description: userProfile.companyDescription || '',
                    companySize: userProfile.companySize || '',
                    companyIndustry: userProfile.companyIndustry || ''
                  });
                }
                setSaveMessage(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6 opacity-60">
        <div className="flex items-center mb-6">
          <Users className="w-5 h-5 mr-2 text-gray-400 dark:text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400">Team Members</h3>
          <span className="ml-3 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
            Coming Soon
          </span>
        </div>
        
        <div className="space-y-4">
          <p className="text-gray-500 dark:text-gray-400">Manage your team members and their permissions.</p>
          <div className="flex items-center space-x-3">
            <Button disabled className="cursor-not-allowed">
              Invite Team Member
            </Button>
            <Button variant="outline" size="sm" disabled className="cursor-not-allowed">
              Manage Roles
            </Button>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">
            Team management features are currently in development and will be available soon.
          </p>
        </div>
      </Card>

      {/* Organization Stats */}
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <Settings className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Organization Statistics</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">1</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Active Members</div>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">0</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Active Projects</div>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {userProfile?.createdAt ? Math.floor((Date.now() - new Date(userProfile.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">Days Active</div>
          </div>
        </div>
      </Card>
    </div>
  );
} 